import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import {
  getGoalDashboardStats,
  getGoalProgressIndicators,
  getSurveyDashboardData,
} from './api';
import type { DashboardProps, GoalStats, SurveyData, SnackbarState } from './types';
import GoalMetricsSection from './components/GoalMetricsSection';
import SurveyMetricsSection from './components/SurveyMetricsSection';
import { todayLocalDateParam } from '@/utils/apiDate';

const ImpactDashboard: React.FC<DashboardProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

  const [goalStats, setGoalStats] = useState<GoalStats | null>(null);
  const [goalIndicators, setGoalIndicators] = useState<any[]>([]);
  const [goalLoading, setGoalLoading] = useState({ stats: false, indicators: false });
  const [goalError, setGoalError] = useState<string | null>(null);

  const [surveyData, setSurveyData] = useState<SurveyData | null>(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyError, setSurveyError] = useState<string | null>(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchAllData = useCallback(async () => {
    try {
      setGoalLoading({ stats: true, indicators: true });
      setSurveyLoading(true);
      setGoalError(null);
      setSurveyError(null);

      const [statsResponse, indicatorsResponse, surveyResponse] = await Promise.allSettled([
        getGoalDashboardStats(),
        getGoalProgressIndicators(),
        getSurveyDashboardData(),
      ]);

      const goalErrors: string[] = [];

      if (statsResponse.status === 'fulfilled') {
        const stats = statsResponse.value as Record<string, number>;
        setGoalStats({
          companyTotalGoals: stats.companyTotalGoals || 0,
          companyCompletedGoals: stats.companyCompletedGoals || 0,
          companyOnTrackGoals: stats.companyOnTrackGoals || 0,
          companyAtRiskGoals: stats.companyAtRiskGoals || 0,
          companyOverdueGoals: stats.companyOverdueGoals || 0,
          userTotalGoals: stats.userTotalGoals || stats.assignedTotalGoals || 0,
          userCompletedGoals: stats.userCompletedGoals || stats.assignedCompletedGoals || 0,
          userAverageProgress: stats.userAverageProgress || stats.averageProgress || 0,
          userOnTrackGoals: stats.userOnTrackGoals || 0,
          userCompletionRate: stats.userCompletionRate || 0,
          assignedTotalGoals: stats.assignedTotalGoals || 0,
          assignedCompletionRate: stats.assignedCompletionRate || 0,
          engagementScore: stats.engagementScore || 0,
          activeGoalStreak: stats.activeGoalStreak || 0,
        });
      } else {
        goalErrors.push(String(statsResponse.reason?.message || t('performance.failedLoadGoalStats')));
      }

      if (indicatorsResponse.status === 'fulfilled') {
        const indicators = (indicatorsResponse.value as Record<string, unknown>[]) || [];
        setGoalIndicators(
          indicators.map((indicator, index: number) => ({
            id: String(indicator.id || `goal-indicator-${index}`),
            title: String(indicator.title || indicator.name || `Goal ${index + 1}`),
            subtitle: String(indicator.subtitle || ''),
            value: Number(indicator.progress || indicator.value || indicator.completionRate || 0),
            period: String(indicator.period || indicator.timeFrame || 'Overall'),
            description: String(indicator.description || ''),
            trend: (indicator.trend as 'up' | 'down' | 'stable') || 'stable',
            change: String(indicator.change || 'No data'),
            icon: String(indicator.icon || 'Target'),
          }))
        );
      } else {
        goalErrors.push(String(indicatorsResponse.reason?.message || t('performance.failedLoadIndicators')));
      }

      if (goalErrors.length > 0) {
        setGoalError(goalErrors.join('; '));
      }

      if (surveyResponse.status === 'fulfilled') {
        const surveyStats = surveyResponse.value as Record<string, unknown>;
        const totalResponses = Number(surveyStats?.totalResponses || 0);
        const promoters = Number(surveyStats?.promoters || 0);
        const passives = Number(surveyStats?.passives || 0);
        const detractors = Number(surveyStats?.detractors || 0);
        const nps =
          totalResponses > 0
            ? Math.round(((promoters - detractors) / totalResponses) * 100)
            : 0;
        const participationRate =
          Number(surveyStats?.responseRate) ||
          (surveyStats?.totalSurveysSent
            ? Math.round((totalResponses / Number(surveyStats.totalSurveysSent)) * 100)
            : 0);

        setSurveyData({
          totalResponses,
          promoters,
          passives,
          detractors,
          nps,
          participationRate,
          surveysTaken: totalResponses,
          feedbackScore: Number(surveyStats?.averageScore || 0),
          month: String(surveyStats?.month || todayLocalDateParam().slice(0, 7)),
          categoryBreakdown: (surveyStats?.categoryBreakdown as Record<string, unknown>) || {},
          insufficientData: surveyStats?.insufficientData === true,
        });
      } else {
        setSurveyError(String(surveyResponse.reason?.message || t('performance.failedLoadSurveyData')));
      }
    } catch {
      setSnackbar({
        open: true,
        message: t('performance.dashboardRefreshFailed'),
        severity: 'error',
      });
    } finally {
      setGoalLoading({ stats: false, indicators: false });
      setSurveyLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAllData();

    const autoRefreshInterval = setInterval(() => {
      fetchAllData();
    }, 120000);

    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [refreshTrigger, fetchAllData]);

  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  const handleRefreshAllData = () => {
    setRefreshTrigger((prev) => prev + 1);
    setSnackbar({
      open: true,
      message: t('performance.refreshingDashboard'),
      severity: 'success',
    });
  };

  const handleNavigateToGoalAlignment = useCallback(() => {
    navigate('/employee-impact/goals');
  }, [navigate]);

  const handleNavigateToSurveys = useCallback(() => {
    navigate('/employee-impact/enps');
  }, [navigate]);

  return (
    <PageLayout
      title={t('performance.dashboardTitle')}
      description={t('performance.dashboardDescription')}
      icon={TrendingUp}
      accentColor="rose"
      maxWidth="1600px"
      headerActions={
        <Button
          variant="outline"
          onClick={handleRefreshAllData}
          disabled={goalLoading.stats || goalLoading.indicators || surveyLoading}
          iconName="RefreshCw"
        >
          {t('performance.refreshAllData')}
        </Button>
      }
    >
      {snackbar.open && (
        <div
          className={cn(
            'fixed bottom-4 end-4 z-50 rounded-lg px-4 py-3 shadow-lg text-sm font-medium transition-opacity',
            snackbar.severity === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-emerald-600 text-white'
          )}
        >
          {snackbar.message}
        </div>
      )}

      <Card className="mb-6">
        <CardContent>
          <h2 className="text-lg font-medium text-foreground mb-6">{t('performance.progressIndicators')}</h2>
          <GoalMetricsSection
            goalStats={goalStats}
            goalIndicators={goalIndicators}
            loading={goalLoading}
            error={goalError}
            onNavigateToGoals={handleNavigateToGoalAlignment}
            onRetry={fetchAllData}
          />
          <SurveyMetricsSection
            surveyData={surveyData}
            loading={surveyLoading}
            error={surveyError}
            onNavigateToSurveys={handleNavigateToSurveys}
            onRetry={fetchAllData}
          />
        </CardContent>
      </Card>
    </PageLayout>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  imageUrl: state.user.imageUrl,
  fullName: state.user.fullName,
});

export default connect(mapStateToProps)(ImpactDashboard);
